import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  prefersReducedMotion,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * Fullscreen zoom dialog — a horizontal Swiper lightbox.
 *
 * Navigation: arrows / keyboard / swipe between media. Click (or pinch / double-tap on
 * touch) zooms into the active image via Swiper's `zoom` module. A thumbnail strip at
 * the bottom mirrors the active slide and jumps to a media on click.
 *
 * The Swiper is initialised lazily on first open and kept alive. `update()` runs on each
 * open (Swiper can't measure while the dialog is `display:none`); after a model-picker
 * morph the host `<swiper-container>` may be replaced — `#setupSwiper` detects the lost
 * instance and re-initialises from the fresh slides.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement & { swiper?: any; initialize?: () => void }} swiper - The <swiper-container>.
 * @property {HTMLElement} thumbnails - The thumbnail strip container.
 * @property {HTMLElement[]} [media] - The <swiper-slide> elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'swiper', 'thumbnails'];

  /** @type {Set<string>} */
  #highResImagesLoaded = new Set();

  /** @type {any} Swiper instance (null until first open / after teardown). */
  #swiper = null;

  /** @type {number} Active media index. */
  #activeIndex = 0;

  /** @type {HTMLElement | null} Element the thumbnail drag-to-scroll is bound to. */
  #thumbDragEl = null;

  /** @type {{ active: boolean, moved: boolean, captured: boolean, startX: number, startScroll: number, pointerId: number }} */
  #thumbDrag = { active: false, moved: false, captured: false, startX: 0, startScroll: 0, pointerId: -1 };

  connectedCallback() {
    super.connectedCallback();
    this.#attachThumbDrag();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#destroySwiper();
    this.#detachThumbDrag();
  }

  /**
   * Opens the zoom dialog at the given media index.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} [event] - The originating pointer event (for the open transition).
   */
  async open(index, event) {
    event?.preventDefault?.();

    const targetIndex = Number(index) || 0;
    this.#activeIndex = targetIndex;

    const reveal = () => {
      this.refs.dialog.showModal();
      this.#setupSwiper(targetIndex);
    };

    /** @type {HTMLElement | null} */
    const sourceImage =
      event?.target instanceof Element
        ? event.target.closest('button, .ax-gallery__main-item, slideshow-slide, li')
        : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage) return reveal();

    const transitionName = 'gallery-item-open';
    sourceImage.style.setProperty('view-transition-name', transitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      reveal();
      sourceImage.style.removeProperty('view-transition-name');
      this.#activeSlideImage()?.style.setProperty('view-transition-name', transitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    this.#activeSlideImage()?.style.removeProperty('view-transition-name');
  }

  /**
   * Initialise the Swiper (first open / after a morph), or just re-measure + jump.
   * @param {number} initialIndex - The slide to land on.
   */
  #setupSwiper(initialIndex) {
    const swiperEl = this.refs.swiper;
    if (!swiperEl) return;

    // Rebind the thumbnail drag-scroll (the strip may have been morphed) and refresh
    // its scrollable state now the dialog is visible (so the grab cursor is accurate).
    this.#attachThumbDrag();
    this.#updateThumbScrollable();

    // Live instance on the current element → re-measure (dialog was hidden) and jump.
    if (swiperEl.swiper && swiperEl.swiper === this.#swiper) {
      this.#swiper.update();
      this.#swiper.zoom?.out?.();
      this.#swiper.slideTo(initialIndex, 0);
      this.#syncThumbnails(initialIndex);
      this.#loadHighResByIndex(initialIndex);
      this.#updateNavState();
      return;
    }

    // First open, or the host element was replaced by a morph → initialise fresh.
    this.#destroySwiper();
    if (typeof swiperEl.initialize !== 'function') return;

    Object.assign(swiperEl, {
      initialSlide: initialIndex,
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 300,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      keyboard: { enabled: true },
      zoom: { maxRatio: 3, toggle: true },
      a11y: {
        enabled: true,
        prevSlideMessage: this.dataset.prevLabel || 'Previous media',
        nextSlideMessage: this.dataset.nextLabel || 'Next media',
      },
    });

    swiperEl.initialize();
    swiperEl.removeAttribute('init');

    const swiper = swiperEl.swiper;
    this.#swiper = swiper;
    if (!swiper) return;

    this.#activeIndex = swiper.realIndex ?? initialIndex;
    swiper.on('slideChange', this.#handleSlideChange);

    this.#syncThumbnails(this.#activeIndex);
    this.#loadHighResByIndex(this.#activeIndex);
    this.#updateNavState();
  }

  /** Sync UI state to the active slide whenever the swiper moves. */
  #handleSlideChange = () => {
    const index = this.#swiper?.realIndex ?? 0;
    this.#activeIndex = index;
    this.#syncThumbnails(index);
    this.#loadHighResByIndex(index);
    this.#updateNavState();
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  };

  /**
   * Mark the active thumbnail and scroll it into view.
   * @param {number} index - The active media index.
   */
  #syncThumbnails(index) {
    const { thumbnails } = this.refs;
    if (!thumbnails) return;

    const buttons = Array.from(thumbnails.querySelectorAll('button'));
    for (const [i, button] of buttons.entries()) {
      button.setAttribute('aria-selected', `${i === index}`);
    }

    const active = buttons[index];
    if (!(active instanceof HTMLElement)) return;

    scrollIntoView(active, {
      ancestor: thumbnails,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'center',
      inline: 'center',
    });
  }

  /**
   * Thumbnail click → jump the swiper to that media.
   * @param {number} index - The index of the thumbnail to select.
   */
  handleThumbnailClick(index) {
    this.#swiper?.slideTo(Number(index));
  }

  /**
   * Thumbnail hover → preload that media's high-resolution image.
   * @param {number} index - The index of the thumbnail hovered.
   */
  handleThumbnailPointerEnter(index) {
    this.#loadHighResByIndex(Number(index));
  }

  /** Navigation arrow → previous media (on:click="/previous"). */
  previous() {
    this.#swiper?.slidePrev();
  }

  /** Navigation arrow → next media (on:click="/next"). */
  next() {
    this.#swiper?.slideNext();
  }

  /** Disable the prev / next arrows at the first / last slide. */
  #updateNavState() {
    const swiper = this.#swiper;
    if (!swiper) return;

    const prev = this.querySelector('.dialog-zoomed-gallery__arrow--prev');
    const next = this.querySelector('.dialog-zoomed-gallery__arrow--next');
    if (prev instanceof HTMLButtonElement) prev.disabled = Boolean(swiper.isBeginning);
    if (next instanceof HTMLButtonElement) next.disabled = Boolean(swiper.isEnd);
  }

  /**
   * Bind (or rebind after a morph) mouse drag-to-scroll on the bottom thumbnail strip.
   * No-ops when already bound to the current element.
   */
  #attachThumbDrag() {
    const el = this.refs.thumbnails ?? null;
    if (this.#thumbDragEl === el) return;

    this.#detachThumbDrag();
    this.#thumbDragEl = el;
    el?.addEventListener('pointerdown', this.#onThumbPointerDown);
  }

  #detachThumbDrag() {
    const el = this.#thumbDragEl;
    if (!el) return;

    el.removeEventListener('pointerdown', this.#onThumbPointerDown);
    el.removeEventListener('pointermove', this.#onThumbPointerMove);
    el.removeEventListener('pointerup', this.#onThumbPointerUp);
    el.removeEventListener('pointercancel', this.#onThumbPointerUp);
    this.#thumbDragEl = null;
  }

  /** Toggle the grab cursor only while the strip actually overflows. */
  #updateThumbScrollable() {
    const el = this.refs.thumbnails;
    if (!el) return;
    el.classList.toggle('is-scrollable', el.scrollWidth > el.clientWidth + 1);
  }

  /** @param {PointerEvent} event */
  #onThumbPointerDown = (event) => {
    // Mouse / pen only — touch keeps native momentum scrolling.
    if (event.pointerType === 'touch') return;

    const el = this.#thumbDragEl;
    if (!el || el.scrollWidth <= el.clientWidth) return;

    this.#thumbDrag = {
      active: true,
      moved: false,
      captured: false,
      startX: event.clientX,
      startScroll: el.scrollLeft,
      pointerId: event.pointerId,
    };
    el.addEventListener('pointermove', this.#onThumbPointerMove);
    el.addEventListener('pointerup', this.#onThumbPointerUp);
    el.addEventListener('pointercancel', this.#onThumbPointerUp);
    // No capture / grabbing cursor yet — wait to see if this becomes a drag, so a plain
    // click still reaches the thumbnail button (pointer capture would steal the click).
  };

  /** @param {PointerEvent} event */
  #onThumbPointerMove = (event) => {
    if (!this.#thumbDrag.active) return;

    const el = this.#thumbDragEl;
    if (!el) return;

    const dx = event.clientX - this.#thumbDrag.startX;
    if (!this.#thumbDrag.moved) {
      if (Math.abs(dx) <= 4) return;
      // Threshold crossed → it's a drag: capture the pointer and block the click.
      this.#thumbDrag.moved = true;
      el.classList.add('is-dragging', 'is-grabbing');
      try {
        el.setPointerCapture(event.pointerId);
        this.#thumbDrag.captured = true;
      } catch (_) {
        // drag still works without capture
      }
    }
    el.scrollLeft = this.#thumbDrag.startScroll - dx;
  };

  /** @param {PointerEvent} event */
  #onThumbPointerUp = (event) => {
    const el = this.#thumbDragEl;
    if (!el) return;

    el.removeEventListener('pointermove', this.#onThumbPointerMove);
    el.removeEventListener('pointerup', this.#onThumbPointerUp);
    el.removeEventListener('pointercancel', this.#onThumbPointerUp);
    if (this.#thumbDrag.captured) {
      try {
        el.releasePointerCapture(event.pointerId);
      } catch (_) {
        // already released
      }
    }
    el.classList.remove('is-grabbing');

    const moved = this.#thumbDrag.moved;
    this.#thumbDrag.active = false;

    // Keep the strip click-proof until the click that follows pointerup is swallowed
    // (only when an actual drag happened, so a plain click still selects a thumbnail).
    if (moved) {
      requestAnimationFrame(() => el.classList.remove('is-dragging'));
    } else {
      el.classList.remove('is-dragging');
    }
  };

  /**
   * Closes the dialog, animating back to the gallery's main image when possible.
   */
  async close() {
    const activeImage = this.#activeSlideImage();
    const galleryItem = this.#galleryMainItem(this.#activeIndex);
    const canTransition =
      supportsViewTransitions() &&
      !isLowPowerDevice() &&
      activeImage &&
      galleryItem &&
      galleryItem.offsetParent !== null;

    if (!canTransition) return this.closeDialog();

    const transitionName = 'gallery-item-close';
    const focalPoint = galleryItem.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    activeImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      activeImage.style.removeProperty('view-transition-name');
      galleryItem.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    galleryItem.style.removeProperty('view-transition-name');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  /** Reset zoom, close the native dialog and notify listeners. */
  closeDialog() {
    this.#swiper?.zoom?.out?.();
    this.refs.dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.close();
  }

  /**
   * Loads a high-resolution image for a specific slide, swapping it in once decoded.
   * Public so the base media-gallery can also drive it.
   * @param {HTMLElement} mediaContainer - The slide element.
   */
  loadHighResolutionImage(mediaContainer) {
    const image = mediaContainer?.querySelector('img.product-media__image');
    if (!(image instanceof HTMLImageElement)) return;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };
    newImage.src = highResolutionUrl;
  }

  /** @param {number} index */
  #loadHighResByIndex(index) {
    const slide = this.#slides()[index];
    if (slide) this.loadHighResolutionImage(slide);
  }

  /** @returns {HTMLElement[]} The slide elements, queried live (morph-safe). */
  #slides() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.refs.swiper?.querySelectorAll('swiper-slide') ?? [])
    );
  }

  /** @returns {HTMLImageElement | null} The <img> of the active slide. */
  #activeSlideImage() {
    const image = this.#slides()[this.#activeIndex]?.querySelector('img.product-media__image');
    return image instanceof HTMLImageElement ? image : null;
  }

  /**
   * The gallery's desktop main-image item at the given index (close transition target).
   * @param {number} index
   * @returns {HTMLElement | null}
   */
  #galleryMainItem(index) {
    const item = this.closest('media-gallery')?.querySelectorAll('.ax-gallery__main-item')?.[index];
    return item instanceof HTMLElement ? item : null;
  }

  /** Tear down the Swiper instance and its listeners. */
  #destroySwiper() {
    if (!this.#swiper) return;
    try {
      this.#swiper.off('slideChange', this.#handleSlideChange);
      this.#swiper.destroy(true, true);
    } catch (_) {
      // noop
    }
    this.#swiper = null;
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
